import { useTranslation } from 'react-i18next';
import { hasClinicContactDetails, type ClinicContact } from '@/api/clinic/clinicApi.types';
import { Icon, type IconName } from '@/components/common/Icon';
import { Card } from '@/components/ui/card';
import { formatPhoneNumberForDisplay } from '@/utils/phoneNumbers';

interface ClinicContactCardProps {
  clinicContact: ClinicContact;
}

export function ClinicContactCard({ clinicContact: clinic }: ClinicContactCardProps) {
  const { t } = useTranslation('clients');

  if (hasClinicContactDetails(clinic) === false) {
    return null;
  }

  return (
    <Card as="section">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-9 h-9 rounded-xl bg-zoovpalelavender text-zooviolet flex items-center justify-center shrink-0">
          <Icon name="Building" />
        </span>
        <div className="min-w-0">
          <h2 className="text-sm font-bold text-zooindigo">{t('clinic.title')}</h2>
          <p className="text-xs text-zoomuted truncate">{clinic.name}</p>
        </div>
      </div>
      <ul className="space-y-2 text-[13px]">
        {clinic.contactPhone === null ? null : (
          <ContactLine
            iconName="Phone"
            href={'tel:' + clinic.contactPhone}
            label={formatPhoneNumberForDisplay(clinic.contactPhone)}
          />
        )}
        {clinic.contactEmail === null ? null : (
          <ContactLine
            iconName="Mail"
            href={'mailto:' + clinic.contactEmail}
            label={clinic.contactEmail}
          />
        )}
        {clinic.contactWebsite === null ? null : (
          <ContactLine
            iconName="Globe"
            href={clinic.contactWebsite}
            label={clinic.contactWebsite}
          />
        )}
        {clinic.addressLines.length === 0 ? null : (
          <li className="flex items-start gap-2 text-zooindigo">
            <Icon name="MapPin" className="text-zoomuted mt-0.5 shrink-0" />
            <span>
              {clinic.addressLines.map(function renderLine(line) {
                return (
                  <span key={line} className="block">
                    {line}
                  </span>
                );
              })}
            </span>
          </li>
        )}
      </ul>
    </Card>
  );
}

interface ContactLineProps {
  iconName: IconName;
  href: string;
  label: string;
}

function ContactLine({ iconName, href, label }: ContactLineProps) {
  return (
    <li className="flex items-center gap-2 min-w-0">
      <Icon name={iconName} className="text-zoomuted shrink-0" />
      <a
        href={href}
        target={href.startsWith('http') ? '_blank' : undefined}
        rel="noopener noreferrer"
        className="text-zooprimary hover:underline truncate"
      >
        {label}
      </a>
    </li>
  );
}
